import React from 'react';
import { AcaoConfirmarPdf, IAuditFile } from '../interfaces/IAuditoriaEntradaDados';

interface Props {
  arquivo: IAuditFile;
  ocupado?: boolean;
  onDecidir: (arquivo: IAuditFile, processar: boolean) => void;
  onConfirmar: (arquivo: IAuditFile, acao: AcaoConfirmarPdf) => void;
}

const usd = (valor: number) => `US$ ${valor.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const brl = (valor: number | null | undefined) =>
  valor === null || valor === undefined ? '—' : valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const data = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : 's/ data');

// DEVN3-662 — os dois pontos em que um extrato em PDF espera o usuário, com as mesmas regras do
// extrato do cartão (DEVN3-686): antes de gastar com a leitura por IA (previsão de custo) e
// depois dela, quando a soma dos lançamentos lidos não fecha com os saldos impressos no PDF.
const AuditPdfPanel: React.FC<Props> = ({ arquivo, ocupado, onDecidir, onConfirmar }) => {
  const pdf = arquivo.pdf;
  if (!pdf) return null;

  if (arquivo.status === 'AWAITING_COST_DECISION') {
    return (
      <div className='audit-pdf-panel'>
        <div className='fw-bold mb-1'>Este PDF será lido por IA. Confira a previsão de custo antes de processar.</div>
        <div className='d-flex flex-wrap gap-4 mb-2'>
          <span>Páginas: <b>{pdf.totalPaginas}</b></span>
          <span>Custo previsto: <b>{usd(pdf.custoProvavelUsd)}</b></span>
          <span>Custo máximo: <b>{usd(pdf.custoMaximoUsd)}</b></span>
        </div>
        <div className='d-flex gap-2'>
          <button type='button' className='btn btn-sm btn-primary py-1' disabled={ocupado} onClick={() => onDecidir(arquivo, true)}>
            Processar
          </button>
          <button type='button' className='btn btn-sm btn-light-danger py-1' disabled={ocupado} onClick={() => onDecidir(arquivo, false)}>
            Descartar
          </button>
        </div>
      </div>
    );
  }

  const conferencia = pdf.conferencia;
  if (arquivo.status !== 'AWAITING_CONFIRMATION' || !conferencia) return null;

  const diferenca =
    conferencia.saldoFinalInformado !== null && conferencia.saldoFinalCalculado !== null
      ? conferencia.saldoFinalInformado - conferencia.saldoFinalCalculado
      : null;

  return (
    <div className='audit-pdf-panel'>
      <div className='fw-bold mb-1'>
        {conferencia.tipo === 'SEM_SALDO'
          ? 'Leitura sem conferência: o PDF não traz saldo para comparar.'
          : `A soma dos lançamentos lidos não fechou com o saldo do PDF em ${conferencia.totalDivergencias} ponto(s).`}
      </div>
      <div className='d-flex flex-wrap gap-4 mb-1'>
        <span>Lançamentos: <b>{conferencia.quantidade}</b></span>
        <span>Entradas: <b>{brl(conferencia.totalEntradas)}</b></span>
        <span>Saídas: <b>{brl(conferencia.totalSaidas)}</b></span>
        {conferencia.tipo !== 'SEM_SALDO' && (
          <>
            <span>Saldo final no PDF: <b>{brl(conferencia.saldoFinalInformado)}</b></span>
            <span>Calculado: <b>{brl(conferencia.saldoFinalCalculado)}</b></span>
            {diferenca !== null && <span>Diferença: <b className='text-danger'>{brl(diferenca)}</b></span>}
          </>
        )}
      </div>
      {conferencia.divergencias.slice(0, 3).map((d, i) => (
        <div key={i} className='text-muted'>
          {data(d.data)} · {d.historico || 'saldo'}: PDF {brl(d.saldoInformado)} × lido {brl(d.saldoCalculado)}
        </div>
      ))}
      {conferencia.linhasNaoConferidas > 0 && conferencia.tipo !== 'SEM_SALDO' && (
        <div className='text-muted'>
          {conferencia.linhasNaoConferidas} lançamento(s) depois do último saldo impresso, sem como conferir.
        </div>
      )}
      <div className='d-flex gap-2 mt-2'>
        <button type='button' className='btn btn-sm btn-primary py-1' disabled={ocupado} onClick={() => onConfirmar(arquivo, 'IMPORTAR')}>
          Importar mesmo assim
        </button>
        {conferencia.lotesDivergentes.length > 0 && (
          <button type='button' className='btn btn-sm btn-light-primary py-1' disabled={ocupado} onClick={() => onConfirmar(arquivo, 'RELER')}>
            Reler páginas divergentes
          </button>
        )}
        <button type='button' className='btn btn-sm btn-light-danger py-1' disabled={ocupado} onClick={() => onConfirmar(arquivo, 'DESCARTAR')}>
          Descartar
        </button>
      </div>
    </div>
  );
};

export default AuditPdfPanel;
